/**
 * Browser mirror of functions/lib/plainAskLabels.js (Create React App refuses
 * imports from outside src/). src/utils/plainAskLabels.test.js runs the same
 * fixtures through both files; change one, change the other.
 *
 * The words a broker reads, never the advisor's sheet heading.
 *
 * THE LIVE DEFECT, 21 Sep 2026: a real account's opening asked brokers for
 * "- Rent/SF /Yr", "- Ops Ex / SF", "- Drive Ins" and "- Ceiling Ht". Those
 * are column names. composeGeneratedScriptWithAskContract passed each Ask
 * column label straight through as a bullet. The backend's deterministic asks
 * already name the thing in plain words (email_automation/processing.py
 * _AUTOMATIC_REQUEST_FIELD_LABELS); every composed opening and sibling note now
 * does too.
 *
 * Browser mirror: src/utils/plainAskLabels.js. src/utils/plainAskLabels.test.js
 * runs the same fixtures through both and fails the moment they disagree.
 */

// Checked in order: the first group whose alias appears in the heading wins.
// Expenses come before rent so "NNN Rent" style headings read as the NNNs.
export const PLAIN_ASK_LABEL_GROUPS = [
  { plain: 'the NNNs', aliases: ['ops ex', 'op ex', 'opex', 'operating expenses', 'operating expense', 'nnn', 'nnns', 'cam', 'expenses'] },
  { plain: 'the asking rent', aliases: ['rent', 'rental rate', 'lease rate', 'asking rate', 'base rate'] },
  { plain: 'the office square footage', aliases: ['office sf', 'office size', 'office area', 'office'] },
  { plain: 'the total square footage', aliases: ['total sf', 'available sf', 'square footage', 'sq ft', 'sqft', 'total size', 'size sf', 'sf'] },
  { plain: 'the number of drive-ins', aliases: ['drive ins', 'drive in', 'driveins', 'drive in doors', 'grade level', 'ramp doors', 'ramp door'] },
  { plain: 'the number of docks', aliases: ['docks', 'dock', 'loading docks', 'dock doors', 'dock high'] },
  { plain: 'the clear height', aliases: ['ceiling ht', 'ceiling height', 'ceiling', 'clear height', 'clear ht', 'clearance', 'eave height'] },
  { plain: 'power (amps/volts/phase)', aliases: ['power', 'electrical', 'amperage', 'amps', 'voltage', 'volts', 'phase'] },
  { plain: 'the year built', aliases: ['year built', 'construction year', 'yr built'] },
  { plain: 'a flyer or floor plan', aliases: ['flyer', 'flier', 'brochure', 'marketing package', 'floor plan', 'floorplan', 'om'] },
  { plain: 'the lease term', aliases: ['lease term', 'term'] },
  { plain: 'the zoning', aliases: ['zoning'] },
  { plain: 'the parking', aliases: ['parking'] },
  { plain: 'when it could be ready', aliases: ['availability date', 'date available', 'available date', 'possession', 'occupancy'] },
];

function tokenize(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function containsPhrase(text, phrase) {
  return ` ${text} `.includes(` ${phrase} `);
}

// Unit fragments that belong in a sheet heading and never in a sentence.
const UNIT_FRAGMENT_PATTERN = /\s*(?:\/\s*(?:sf|sq\.?\s*ft|yr|year|mo|month|annual|ann)\b|\bper\s+(?:sf|sq\.?\s*ft|yr|year|month)\b|\((?:[^)]*)\)|[#$%:])/giu;

function cleanUnknownHeading(label) {
  const words = String(label || '')
    .replace(UNIT_FRAGMENT_PATTERN, ' ')
    .replace(/[_/|\\]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    // Keep an acronym ("HVAC", "TI") as typed; lower-case every Title-case word.
    .map(word => (/^[A-Z0-9]{2,}$/.test(word) ? word : word.toLowerCase()));
  if (words.length === 0) return '';
  const text = words.join(' ');
  return /^(?:the|a|an|any)\b/i.test(text) ? text : `the ${text}`;
}

/**
 * The plain-language ask for one Ask column heading.
 *
 * @param {string} label the Ask column heading exactly as the sheet names it
 * @returns {string} the words to put in front of a broker ('' for no label)
 */
export function knownPlainAskLabel(label) {
  const text = tokenize(label);
  if (!text) return '';
  const group = PLAIN_ASK_LABEL_GROUPS.find(entry => (
    entry.aliases.some(alias => containsPhrase(text, alias))
  ));
  return group ? group.plain : '';
}

export function plainAskLabel(label) {
  if (!tokenize(label)) return '';
  return knownPlainAskLabel(label) || cleanUnknownHeading(label);
}

/**
 * Plain asks for a list of headings, in order, without repeating one thing
 * ("Rent/SF/Yr" and "Asking Rent" are one ask).
 */
export function plainAskLabels(labels) {
  const seen = new Set();
  const result = [];
  for (const label of Array.isArray(labels) ? labels : []) {
    if (typeof label !== 'string') continue;
    const plain = plainAskLabel(label);
    if (!plain || seen.has(plain)) continue;
    seen.add(plain);
    result.push(plain);
  }
  return result;
}

/** "a, b and c" - how a person lists two or more things in a sentence. */
export function joinAsSentenceList(items) {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}
